import classNames from 'classnames';
import React, {
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  DetectOverflowOptions,
  Middleware,
  MiddlewareData,
  Padding,
  Placement,
  ShiftOptions,
  UseFloatingReturn,
  detectOverflow,
} from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  hide,
  limitShift,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { isTouchDevice } from '../constants/os';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useControlledState from '../utils/useControlledState';
import type { HoverDelay } from '../utils/useHover';
import useHover from '../utils/useHover';
import * as styles from './PopOverPrimitive.module.scss';
import Slot from './Slot';

const DETECT_OVERFLOW_OPTIONS: Partial<
  Parameters<typeof detectOverflow>[1] & ShiftOptions
> = {
  padding: 0,
  boundary: [],
  altBoundary: false,
  mainAxis: true,
  crossAxis: false,
  limiter: limitShift(),
};

type TriggerType = 'click' | 'hover' | 'clickOrHover' | 'none';

interface PortalContainerProps {
  children: React.ReactNode;
  noPortal: boolean | undefined;
}

const PortalContainer = ({ children, noPortal }: PortalContainerProps) => {
  if (noPortal) return <>{children}</>;

  return createPortal(children, document.body);
};

interface PopOverContextValue {
  hoverTriggerEventHandlers: {
    onMouseEnter: React.MouseEventHandler;
    onMouseLeave: React.MouseEventHandler;
  } | null;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  id: string;
  trigger: TriggerType;
  floatingStyles: React.CSSProperties;
  refs: UseFloatingReturn['refs'];
  actualPlacement: Placement;
  arrowData: MiddlewareData['arrow'];
  arrowRef: React.RefObject<SVGSVGElement | null>;
  hideData: MiddlewareData['hide'];
  shouldUseTouch: boolean;
  isHidden: boolean;
}

const PopOverContext = React.createContext<PopOverContextValue | null>(null);
const useOptionalPopOverContext = () => useContext(PopOverContext);

export const usePopOverContext = () => {
  const context = useOptionalPopOverContext();
  if (!context) {
    throw new Error(
      'PopOverPrimitive compound components cannot be rendered outside the PopOverPrimitive component',
    );
  }
  return context;
};

type PopOverPrimitiveProps = {
  children: React.ReactNode;
  trigger: TriggerType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**  @deprecated use enabledForTouch instead */
  enabledForSmallSize?: boolean;
  enabledForTouch?: boolean;
  offset?: number;
  placement?: Placement;
  autoUpdateAnimationFrame?: boolean;
  screenPadding?: Padding;
  noFlip?: boolean;
  hideWhenReferenceHidden?: boolean;
  hoverDelay?: HoverDelay;
  hoverGroupId?: string;
  getAdditionalMiddleware?: (
    options: Partial<DetectOverflowOptions>,
  ) => Middleware[];
  hidden?: boolean;
  anchorRef?: React.RefObject<Element | null>;
  hoverRef?: React.RefObject<{
    setHover: (idOrSetter: string | null) => void;
  } | null>;
};

const PopOverPrimitive = ({
  children,
  trigger,
  enabledForSmallSize,
  enabledForTouch,
  offset: offsetAmount,
  placement = 'top',
  hoverGroupId,
  autoUpdateAnimationFrame,
  screenPadding,
  noFlip,
  hoverRef,
  hideWhenReferenceHidden,
  hoverDelay = 'slow',
  getAdditionalMiddleware,
  hidden = false,
  anchorRef,
  ...props
}: PopOverPrimitiveProps) => {
  const id = useId();
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  let actualTrigger = trigger;
  if (isMobile && trigger === 'hover' && enabledForSmallSize) {
    actualTrigger = 'click';
  }

  const shouldUseTouch = (isTouchDevice && enabledForTouch) ?? false;
  if (shouldUseTouch && trigger === 'hover') {
    actualTrigger = 'click';
  }

  const [isOpen, setIsOpen] = useControlledState({
    defaultValue: false,
    value: props.open,
    onValueChange: props.onOpenChange,
  });

  const middleware = useMemo(() => {
    const overflowOptions = {
      ...DETECT_OVERFLOW_OPTIONS,
      padding: screenPadding,
    };

    return [
      offset(offsetAmount),
      shift(overflowOptions),
      noFlip
        ? undefined
        : flip({
            fallbackAxisSideDirection: 'end',
            ...DETECT_OVERFLOW_OPTIONS,
          }),

      ...(getAdditionalMiddleware
        ? getAdditionalMiddleware(overflowOptions)
        : []),
      arrow({ element: arrowRef, padding: 8 }),

      // Hide should always be placed at the end
      // https://floating-ui.com/docs/hide#order
      hideWhenReferenceHidden ? hide() : undefined,
    ];
  }, [
    offsetAmount,
    arrowRef,
    screenPadding,
    noFlip,
    hideWhenReferenceHidden,
    getAdditionalMiddleware,
  ]);

  const {
    refs,
    floatingStyles,
    middlewareData: { arrow: arrowData, hide: hideData },
    placement: actualPlacement,
    update,
  } = useFloating({
    placement,
    middleware,
  });

  // We can't use `whileElementsMounted` here because we allow the popOver
  // to be hidden with the `hidden` prop. This prop is used to hide the popOver
  // with CSS display: none instead of unmounting it. This is useful because
  // in some instances, we want to already render the children of the floating
  // element before we show them, e.g. because we want a child component first
  // to load some data before we show it.
  //
  // See https://floating-ui.com/docs/usefloating#whileelementsmounted
  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current) {
      return undefined;
    }

    return autoUpdate(refs.reference.current, refs.floating.current, update, {
      animationFrame: autoUpdateAnimationFrame,
    });
  }, [refs, update, isOpen, autoUpdateAnimationFrame, hidden]);

  useLayoutEffect(() => {
    if (anchorRef) {
      refs.setReference(anchorRef.current);
    }
  }, [refs, anchorRef]);

  const isHoverActionAllowed =
    trigger === 'hover' || trigger === 'clickOrHover';

  const internalIsOpenRef = useRef(isOpen);
  const { onMouseEnter, onMouseLeave, setHover } = useHover({
    hoverGroupId,
    hoverDelay,
    onHoverChange(hoveringId, previousHoveringId) {
      if (!isHoverActionAllowed) return;

      if (hoveringId === id) {
        setIsOpen(true);
        internalIsOpenRef.current = true;
      } else if (previousHoveringId === id) {
        setIsOpen(false);
        internalIsOpenRef.current = false;
      }
    },
  });

  useImperativeHandle(hoverRef, () => ({
    setHover,
  }));

  useEffect(() => {
    if (!isHoverActionAllowed) return;

    if (isOpen !== internalIsOpenRef.current) {
      setHover(isOpen ? id : null);
    }
    internalIsOpenRef.current = isOpen;
  }, [id, isOpen, setHover, isHoverActionAllowed]);

  useEffect(() => () => onMouseLeave(id), [id, onMouseLeave]);

  const hoverTriggerEventHandlers = useMemo(() => {
    if (!isHoverActionAllowed) return null;

    return {
      onMouseEnter: onMouseEnter.bind(null, id),
      onMouseLeave: onMouseLeave.bind(null, id),
    };
  }, [onMouseLeave, onMouseEnter, id, isHoverActionAllowed]);

  const contextValue = useMemo(
    () => ({
      setIsOpen,
      isOpen,
      id,
      trigger: actualTrigger,
      actualPlacement,
      floatingStyles,
      refs,
      arrowData,
      arrowRef,
      hideData,
      shouldUseTouch,
      isHidden: hidden,
      hoverTriggerEventHandlers,
    }),
    [
      setIsOpen,
      isOpen,
      id,
      actualTrigger,
      actualPlacement,
      floatingStyles,
      refs,
      arrowData,
      arrowRef,
      hideData,
      shouldUseTouch,
      hidden,
      hoverTriggerEventHandlers,
    ],
  );

  return (
    <PopOverContext.Provider value={contextValue}>
      {children}
    </PopOverContext.Provider>
  );
};

const CLICK_EVENTS = new WeakSet<Event>();

interface PopOverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  /**  @deprecated use asChild instead */
  display?: 'block' | 'inline-block' | 'inline';
  'aria-describedby'?: string;
  preventCloseOnClickOutside?: boolean;
  onMouseDown?: () => void;
}

const PopOverTrigger = ({
  children,
  asChild,
  display,
  ['aria-describedby']: ariaDescribedBy,
  preventCloseOnClickOutside,
  onMouseDown: providedOnMouseDown,
}: PopOverTriggerProps) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const {
    shouldUseTouch,
    trigger,
    isOpen,
    setIsOpen,
    refs,
    hoverTriggerEventHandlers,
  } = usePopOverContext();

  useLayoutEffect(() => {
    if (refs.reference.current == null) {
      refs.setReference(triggerRef.current);
    }
  }, [refs]);

  const commonProps = {
    ...hoverTriggerEventHandlers,
    onMouseDown: providedOnMouseDown,
    ref: triggerRef,
    'aria-describedby': ariaDescribedBy,
  };

  const triggerContainerClassName = classNames(styles.triggerContainer, {
    [styles.inlineBlock]: display == null || display === 'inline-block',
    [styles.inline]: display === 'inline',
    [styles.block]: display === 'block',
  });

  const onClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {trigger === 'hover' || trigger === 'none' ? (
        <>
          {asChild ? (
            <Slot {...commonProps}>{children}</Slot>
          ) : (
            <div {...commonProps} className={triggerContainerClassName}>
              {children}
            </div>
          )}
        </>
      ) : (
        <ClickOutsideHandler
          {...commonProps}
          onClickOutside={(e) => {
            if (
              !preventCloseOnClickOutside &&
              isOpen &&
              !CLICK_EVENTS.has(e) &&
              !triggerRef.current?.contains(e.target as Node)
            ) {
              setIsOpen(false);
            }
          }}
          {...(shouldUseTouch
            ? {
                onTouchStart: onClick,
              }
            : {
                onClick,
              })}
          asChild={asChild}
          className={asChild ? undefined : triggerContainerClassName}
        >
          {children}
        </ClickOutsideHandler>
      )}
    </>
  );
};

type PopOverAnchorProps = {
  children: React.ReactNode;
};

const PopOverAnchor = ({ children }: PopOverAnchorProps) => {
  const context = useOptionalPopOverContext();
  if (context == null) return children;

  return <Slot ref={context?.refs.setReference}>{children}</Slot>;
};

interface PopOverFloatingProps {
  children: React.ReactNode;
  className?: string;
  noPortal?: boolean;
  interactionDisabled?: boolean;
  minHeight?: number;
  role?: string;
  minWidth?: number;
}

const PopOverFloating = React.forwardRef<HTMLDivElement, PopOverFloatingProps>(
  (
    {
      children,
      className,
      noPortal,
      interactionDisabled,
      minHeight,
      role = 'tooltip',
      minWidth,
    },
    ref,
  ) => {
    const {
      id,
      isOpen,
      refs,
      floatingStyles,
      hideData,
      hoverTriggerEventHandlers,
      isHidden,
    } = usePopOverContext();

    return (
      <PortalContainer noPortal={noPortal}>
        {isOpen && (
          <Slot ref={ref}>
            <div
              role={role}
              id={id}
              ref={refs.setFloating}
              style={{ ...floatingStyles, minHeight, minWidth }}
              className={classNames(styles.floatingContainer, className, {
                [styles.isReferenceHidden]: hideData?.referenceHidden,
                [styles.isInteractionDisabled]: interactionDisabled,
                [styles.isHidden]: isHidden,
              })}
              {...hoverTriggerEventHandlers}
            >
              {children}
            </div>
          </Slot>
        )}
      </PortalContainer>
    );
  },
);

interface PopOverArrowProps
  extends Omit<React.SVGAttributes<SVGSVGElement>, 'style' | 'visibility'> {
  children: React.ReactNode;
  height: number;
  width: number;
  offset?: number;
}

const PopOverArrow = ({
  children,
  height,
  width,
  offset: offsetAmount = 0,
  ...rest
}: PopOverArrowProps) => {
  const { actualPlacement, arrowData, arrowRef, isHidden, refs } =
    usePopOverContext();
  const [isPointingAtReference, setIsPointingAtReference] = useState(true);
  const axis = arrowData?.x != null ? 'x' : 'y';

  useEffect(() => {
    if (isHidden || !arrowData?.centerOffset) {
      setIsPointingAtReference(true);
      return;
    }

    const rect = refs.reference.current?.getBoundingClientRect();
    const size = axis === 'x' ? rect?.width : rect?.height;

    setIsPointingAtReference(
      size != null && Math.abs(arrowData.centerOffset) < size / 2,
    );
  }, [arrowData?.centerOffset, axis, isHidden, refs.reference]);

  if (isHidden) return null;

  const arrowPosition = arrowData
    ? { left: arrowData.x, top: arrowData.y }
    : {};

  const cssVars = {
    '--popover-arrow-height': `${height}px`,
    '--popover-arrow-width': `${width}px`,
    '--popover-arrow-offset': `${offsetAmount}px`,
  } as React.CSSProperties;

  return (
    <>
      <div
        className={styles.arrowHoverArea}
        data-placement={actualPlacement}
        style={cssVars}
      />
      <svg
        {...rest}
        data-placement={actualPlacement}
        className={classNames(styles.arrow, rest.className)}
        style={{
          ...arrowPosition,
          ...cssVars,
        }}
        visibility={isPointingAtReference ? 'visible' : 'hidden'}
        ref={arrowRef}
      >
        {children}
      </svg>
    </>
  );
};

interface PopOverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const PopOverContent = React.forwardRef<HTMLDivElement, PopOverContentProps>(
  ({ children, ...rest }, ref) => (
    <div
      {...rest}
      className={classNames(styles.content, rest.className)}
      onMouseDown={(e) => {
        CLICK_EVENTS.add(e.nativeEvent);
        rest.onMouseDown?.(e);
      }}
      onTouchStart={(e) => {
        CLICK_EVENTS.add(e.nativeEvent);
        rest.onTouchStart?.(e);
      }}
      ref={ref}
    >
      {children}
    </div>
  ),
);

PopOverPrimitive.Trigger = PopOverTrigger;
PopOverPrimitive.Floating = PopOverFloating;
PopOverPrimitive.Arrow = PopOverArrow;
PopOverPrimitive.Content = PopOverContent;
PopOverPrimitive.Anchor = PopOverAnchor;

export default PopOverPrimitive;
